import type {
  ExpenseByIdQuery,
  CreateExpenseItemInput,
  UpdateExpenseItemInput,
  RecordType,
} from '../../../__generated__/graphql';
import { ExpenseRecordType } from '../types';

export const REIMBURSEMENT_RECORD_TYPE_KEY = ExpenseRecordType.Reimbursement;

export const EXPENSE_ITEM_RECORD_TYPE_OTHER = 'Other';

export const AUTO_SAVE_DELAY_CLICK_BASED = 400;

export const AUTO_SAVE_DELAY_DEFAULT = 1500;

export const DEBOUNCE_DELAY = 800;

export type SavedExpenseItem = {
  Id: string;
  flair__Note__c: string | null;
  flair__Manual_Amount__c: number | null;
  flair__Amount__c: number | null;
  flair__Tax__c: string | null;
  tax: {
    Id: string;
    Name: string;
    flair__Percentage__c: number | null;
  } | null;
};

export type FormStep = 'upload' | 'details';

export const calculateVatAmount = (amount: number, vatRate: number): number => {
  return (amount * vatRate) / 100;
};

export const findOtherRecordType = (
  recordTypes: RecordType[],
): RecordType | null => {
  if (!recordTypes || recordTypes.length === 0) return null;

  const otherRecordType = recordTypes.find(
    (rt) =>
      rt.Name === EXPENSE_ITEM_RECORD_TYPE_OTHER ||
      rt.Label === EXPENSE_ITEM_RECORD_TYPE_OTHER,
  );

  return otherRecordType || recordTypes[0] || null;
};

export const createDefaultExpenseItemInput = (
  expenseId: string,
  recordTypeId: string,
): CreateExpenseItemInput => ({
  expenseId,
  recordTypeId,
  flair__Note__c: '',
  flair__Manual_Amount__c: 0,
  merchantId: null,
  merchantName: null,
  extraFields: [],
  files: [],
  flair__Address_From__c: null,
  flair__Address_To__c: null,
  flair__Means_Of_Transport__c: null,
});

export const buildExpenseItemUpdateInput = (
  itemId: string,
  updates: Partial<SavedExpenseItem>,
): UpdateExpenseItemInput => {
  const updateInput: UpdateExpenseItemInput = {
    id: itemId,
    RecordTypeId: null,
    flair__Address_From__c: null,
    flair__Address_To__c: null,
    flair__Note__c:
      updates.flair__Note__c !== undefined ? updates.flair__Note__c : undefined,
    flair__Manual_Amount__c:
      updates.flair__Manual_Amount__c !== undefined
        ? updates.flair__Manual_Amount__c
        : undefined,
    flair__Tax__c:
      updates.flair__Tax__c !== undefined ? updates.flair__Tax__c : undefined,
    extraFields: [],
  } as Partial<UpdateExpenseItemInput> as UpdateExpenseItemInput;

  return updateInput;
};

export const mapExpenseItems = (
  expenseItems:
    | NonNullable<ExpenseByIdQuery['expense']>['expenseItems']
    | null
    | undefined,
): SavedExpenseItem[] => {
  if (!expenseItems) return [];

  return expenseItems.map((item) => ({
    Id: item.Id,
    flair__Note__c: item.flair__Note__c,
    flair__Manual_Amount__c: item.flair__Manual_Amount__c,
    flair__Amount__c: item.flair__Amount__c,
    flair__Tax__c: item.flair__Tax__c,
    tax: item.tax
      ? {
          Id: item.tax.Id,
          Name: item.tax.Name || '',
          flair__Percentage__c: item.tax.flair__Percentage__c,
        }
      : null,
  }));
};

export type ReimbursementFormData = {
  description: string;
  expenseDate: Date;
  expenseGroupId: string;
  projectId: string;
  categoryId: string;
  merchantId: string;
};

export const mapExpenseToFormData = (
  expense: NonNullable<ExpenseByIdQuery['expense']>,
): ReimbursementFormData => ({
  description: expense.flair__Description__c || '',
  expenseDate: expense.flair__Date_From__c
    ? new Date(expense.flair__Date_From__c)
    : new Date(),
  expenseGroupId: expense.flair__Expense_Group__c || '',
  categoryId: expense.expenseCategory?.Id || '',
  merchantId: expense.merchant?.Id || '',
  projectId: expense.project?.Id || '',
});

export const CLICK_BASED_AUTO_SAVE_FIELDS = [
  'expenseDate',
  'expenseGroupId',
  'projectId',
  'categoryId',
  'merchantId',
] as const;
